import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BuyerListsQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";

// ---------------------------------------------------------------------------
// GQL document
// ---------------------------------------------------------------------------

const buyerListsQuery = graphql(`
  query buyerLists {
    buyerLists {
      id
      name
      sharingType
      ownerId
      entries {
        id
      }
      permissionActions
    }
  }
`);

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

/** A single buyer list as returned by the GQL query. */
export type BuyerList = BuyerListsQuery["buyerLists"][number];

/**
 * Action names the app-service `BuyerList` resolver returns in
 * `permissionActions`. Mirrors `BUYER_LIST_ACTIONS` in
 * `nodejs/apps/app-service/src/resources/organisations/list/buyerList.constants.ts`
 * — keep them in sync.
 */
export const BUYER_LIST_PERMISSION_ACTIONS = {
  CAN_READ: "can_read",
  CAN_UPDATE: "can_update",
  CAN_DELETE: "can_delete",
  CREATE_ENTRIES: "create_buyer_list_entries",
  DELETE_ENTRIES: "delete_buyer_list_entries",
} as const;

export type BuyerListPermissionAction =
  (typeof BUYER_LIST_PERMISSION_ACTIONS)[keyof typeof BUYER_LIST_PERMISSION_ACTIONS];

export type CategorizedBuyerLists = {
  private: BuyerList[];
  team: BuyerList[];
  all: BuyerList[];
};

// ---------------------------------------------------------------------------
// useBuyerLists
// ---------------------------------------------------------------------------

/**
 * Fetches all buyer lists the current user has read access to.
 *
 * @param permissionFilter  If provided, only lists where the user holds this
 *   action are returned (client-side filter on `permissionActions`).
 */
export function useBuyerLists(
  permissionFilter?: BuyerListPermissionAction,
  options?: UseQueryOptions<BuyerListsQuery, unknown, BuyerListsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(["buyerLists"], buyerListsQuery, [undefined], options);

  const all = permissionFilter
    ? (data?.buyerLists ?? []).filter((l) => l.permissionActions.includes(permissionFilter))
    : data?.buyerLists ?? [];

  const categorized: CategorizedBuyerLists | undefined = data
    ? {
        private: all.filter((l) => l.sharingType === "PRIVATE"),
        team: all.filter((l) => l.sharingType !== "PRIVATE"),
        all,
      }
    : undefined;

  return { data: categorized, ...rest };
}
